import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FiChevronLeft,
  FiChevronRight,
  FiShoppingCart,
  FiTrash2,
  FiTrendingDown,
  FiX,
} from 'react-icons/fi';
import {
  createInventoryMerma,
  getCategories,
  getProducts,
} from '../api';
import CategoryFilterBar from '../components/CategoryFilterBar';
import DocumentHeader from '../components/DocumentHeader';
import InlineLoading from '../components/InlineLoading';
import PosProductCard from '../components/PosProductCard';
import ProductGrid from '../components/ProductGrid';
import ProductSearchInput from '../components/ProductSearchInput';
import useProductSearchSettings from '../hooks/useProductSearchSettings';
import { notify } from '../utils/alertBus';
import {
  buildMermaItemsPayload,
  getActiveMermaProducts,
  getMermaLineKey,
  normalizeMermaQuantity,
  resolveMermaCategory,
  resolveMermaProductCategoryId,
  sanitizeMermaQuantityInput,
} from '../utils/mermaRegistration';
import { normalizePaginatedResponse } from '../utils/pagination';
import { filterProductSearchResults } from '../utils/productSearch';

const PLACEHOLDER_IMAGE = 'https://placehold.co/300';

const formatKg = (value) => `${Number(value || 0).toLocaleString('es-CL', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3,
})} kg`;

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message
  || error?.response?.data?.error
  || error?.message
  || fallback
);

const MermaTrackingBadge = ({ tracksInventory }) => (
  <span
    className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
      tracksInventory
        ? 'bg-amber-100 text-amber-800'
        : 'bg-slate-100 text-slate-600'
    }`}
  >
    {tracksInventory ? 'Con inventario' : 'Solo registro'}
  </span>
);

const MermaCart = ({ lines, onEdit, onRemove, onUpdate, className = '' }) => {
  const totalKg = lines.reduce(
    (sum, line) => sum + normalizeMermaQuantity(line.quantity),
    0
  );

  return (
    <section className={`flex min-h-0 flex-col rounded-xl border border-gray-100 bg-white shadow-sm ${className}`.trim()}>
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Merma por registrar</h2>
          <p className="text-xs text-gray-500">
            {lines.length} {lines.length === 1 ? 'producto' : 'productos'} · {formatKg(totalKg)}
          </p>
        </div>
        <FiShoppingCart className="text-blue-600" size={20} aria-hidden="true" />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {lines.length === 0 ? (
          <div className="flex min-h-48 flex-col items-center justify-center px-6 py-10 text-center">
            <FiShoppingCart className="mb-3 text-gray-300" size={36} aria-hidden="true" />
            <p className="text-sm font-semibold text-gray-700">Aún no hay productos</p>
            <p className="mt-1 text-xs text-gray-500">
              Selecciona un producto y registra los kilos que se desecharán.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {lines.map((line) => (
              <article key={getMermaLineKey(line)} className="p-3">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => onEdit(line)}
                    className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-gray-100"
                    aria-label={`Editar ${line.productName} ${line.varietyName}`}
                  >
                    <img
                      src={line.imageSrc || PLACEHOLDER_IMAGE}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  </button>
                  <div className="min-w-0 flex-1">
                    <button
                      type="button"
                      onClick={() => onEdit(line)}
                      className="block w-full text-left"
                    >
                      <p className="truncate text-sm font-semibold text-gray-900">{line.productName}</p>
                      <p className="truncate text-xs text-gray-500">{line.varietyName}</p>
                    </button>
                    <div className="mt-1">
                      <MermaTrackingBadge tracksInventory={line.tracksInventory} />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(line)}
                    className="rounded-md p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    aria-label={`Quitar ${line.productName} ${line.varietyName}`}
                  >
                    <FiTrash2 size={17} />
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-[minmax(0,8rem)_1fr] gap-2">
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Kilos
                    <div className="relative mt-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={line.quantity}
                        onChange={(event) => onUpdate(line, {
                          quantity: sanitizeMermaQuantityInput(event),
                        })}
                        className="w-full rounded-lg border border-gray-300 px-2 py-2 pr-8 text-right text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                        aria-label={`Kilos de ${line.productName} ${line.varietyName}`}
                      />
                      <span className="pointer-events-none absolute right-2 top-2 text-sm text-gray-400">kg</span>
                    </div>
                  </label>
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Nota opcional
                    <input
                      type="text"
                      value={line.note}
                      onChange={(event) => onUpdate(line, { note: event.target.value })}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm normal-case tracking-normal focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      placeholder="Ej. producto dañado"
                      aria-label={`Nota de ${line.productName} ${line.varietyName}`}
                    />
                  </label>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const VarietyPickerModal = ({ product, category, onClose, onSelect }) => {
  if (!product) return null;
  const varieties = Array.isArray(product.Varieties) ? product.Varieties : [];
  const tracksInventory = Boolean(category?.tracksInventory);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="merma-variety-title"
      className="fixed inset-0 z-[1500] flex items-start justify-center overflow-y-auto bg-slate-950/60 px-4 py-6 md:items-center"
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-gray-50 px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Producto</p>
            <h2 id="merma-variety-title" className="truncate text-xl font-semibold text-gray-900">{product.productName}</h2>
            <div className="mt-1">
              <MermaTrackingBadge tracksInventory={tracksInventory} />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
            aria-label="Cerrar variedades"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          <div className="mb-5 flex items-center gap-4">
            <img
              src={product.productImage || PLACEHOLDER_IMAGE}
              alt={product.productName}
              className="h-24 w-24 rounded-xl border border-gray-200 object-cover"
            />
            <div>
              <p className="text-sm text-gray-500">Categoría</p>
              <p className="font-semibold text-gray-900">{category?.name || 'Sin categoría'}</p>
              <p className="mt-1 text-xs text-gray-500">
                Selecciona la variedad que se desechará.
              </p>
            </div>
          </div>

          {varieties.length ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {varieties.map((variety) => (
                <button
                  key={variety.id}
                  type="button"
                  onClick={() => onSelect(variety)}
                  className="flex items-center gap-3 rounded-xl border border-gray-200 p-3 text-left transition hover:border-blue-300 hover:bg-blue-50 hover:shadow-sm"
                >
                  <img
                    src={variety.varietyImage || product.productImage || PLACEHOLDER_IMAGE}
                    alt=""
                    className="h-14 w-14 rounded-lg bg-gray-100 object-cover"
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">{variety.varietyName}</p>
                    <p className="mt-1 text-xs text-gray-500">Registrar kilos</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
              Este producto no tiene variedades disponibles.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const QuantityModal = ({ draft, onCancel, onConfirm, onDraftChange }) => {
  if (!draft) return null;
  const quantity = normalizeMermaQuantity(draft.quantity);

  return (
    <div className="fixed inset-0 z-[1600] flex items-center justify-center bg-slate-950/60 px-4 py-6">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="merma-quantity-title"
        className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
        onSubmit={(event) => {
          event.preventDefault();
          if (quantity > 0) onConfirm();
        }}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-gray-50 px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Registrar merma</p>
            <h2 id="merma-quantity-title" className="text-lg font-semibold text-gray-900">{draft.productName}</h2>
            <p className="text-sm text-gray-500">{draft.varietyName}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
            aria-label="Cerrar cantidad"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <span className="text-xs text-gray-600">Configuración</span>
            <MermaTrackingBadge tracksInventory={draft.tracksInventory} />
          </div>

          <label className="block text-sm font-semibold text-gray-700">
            Kilos desechados
            <div className="relative mt-1">
              <input
                autoFocus
                type="text"
                inputMode="decimal"
                value={draft.quantity}
                onChange={(event) => onDraftChange({
                  quantity: sanitizeMermaQuantityInput(event),
                })}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 pr-12 text-right text-xl font-semibold focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="0,000"
                aria-label="Kilos desechados"
              />
              <span className="pointer-events-none absolute right-4 top-3.5 font-semibold text-gray-400">kg</span>
            </div>
            <span className="mt-1 block text-xs font-normal text-gray-500">Hasta tres decimales.</span>
          </label>

          <label className="block text-sm font-semibold text-gray-700">
            Nota opcional
            <textarea
              value={draft.note}
              onChange={(event) => onDraftChange({ note: event.target.value })}
              rows="3"
              className="mt-1 w-full resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm font-normal focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              placeholder="Ej. producto dañado o vencido"
            />
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={quantity <= 0}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
          >
            Agregar merma
          </button>
        </div>
      </form>
    </div>
  );
};

const ConfirmMermaModal = ({ isOpen, lines, saving, onCancel, onConfirm }) => {
  if (!isOpen) return null;
  const trackedCount = lines.filter((line) => line.tracksInventory).length;
  const recordOnlyCount = lines.length - trackedCount;
  const totalKg = lines.reduce(
    (sum, line) => sum + normalizeMermaQuantity(line.quantity),
    0
  );

  return (
    <div className="fixed inset-0 z-[1700] flex items-center justify-center bg-slate-950/60 px-4 py-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="merma-confirmation-title"
        className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="border-b border-gray-200 px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-red-600">Confirmar registro</p>
          <h2 id="merma-confirmation-title" className="mt-1 text-xl font-semibold text-gray-900">Registrar {formatKg(totalKg)} de merma</h2>
        </div>
        <div className="space-y-3 p-5 text-sm text-gray-600">
          <p>
            Se guardarán {lines.length} {lines.length === 1 ? 'producto' : 'productos'}.
          </p>
          {trackedCount > 0 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              {trackedCount} {trackedCount === 1 ? 'producto tiene' : 'productos tienen'} inventario y se {trackedCount === 1 ? 'descontará' : 'descontarán'} si {trackedCount === 1 ? 'cuenta' : 'cuentan'} con equivalencia en kg.
            </p>
          )}
          {recordOnlyCount > 0 && (
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700">
              {recordOnlyCount} {recordOnlyCount === 1 ? 'producto quedará' : 'productos quedarán'} solo como registro.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white disabled:opacity-50"
          >
            Volver
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:bg-red-300"
          >
            <FiTrendingDown /> {saving ? 'Guardando...' : 'Confirmar merma'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MermaRegistrationPage = () => {
  const { branchId } = useParams();
  const { searchMode } = useProductSearchSettings();
  const activeBranchRef = useRef(branchId);
  const branchGenerationRef = useRef(0);
  const lastFocusedElementRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [modalProduct, setModalProduct] = useState(null);
  const [quantityDraft, setQuantityDraft] = useState(null);
  const [lines, setLines] = useState([]);
  const [showDesktopCart, setShowDesktopCart] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [saving, setSaving] = useState(false);

  activeBranchRef.current = branchId;

  useEffect(() => {
    branchGenerationRef.current += 1;
    setProducts([]);
    setCategories([]);
    setSearchTerm('');
    setSelectedCategory('all');
    setModalProduct(null);
    setQuantityDraft(null);
    setLines([]);
    setShowDesktopCart(false);
    setShowMobileCart(false);
    setShowConfirmation(false);
    setSaving(false);
  }, [branchId]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const hasOpenDialog = Boolean(
      modalProduct || quantityDraft || showConfirmation || showMobileCart
    );
    if (!hasOpenDialog) {
      const focusTarget = lastFocusedElementRef.current;
      lastFocusedElementRef.current = null;
      if (focusTarget && document.contains(focusTarget)) {
        const restoreTimer = window.setTimeout(() => focusTarget.focus?.(), 0);
        return () => window.clearTimeout(restoreTimer);
      }
      return undefined;
    }

    if (!lastFocusedElementRef.current) {
      lastFocusedElementRef.current = document.activeElement;
    }

    const getActiveDialog = () => {
      const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
      return dialogs[dialogs.length - 1] || null;
    };
    const focusTimer = window.setTimeout(() => {
      const dialog = getActiveDialog();
      if (!dialog || dialog.contains(document.activeElement)) return;
      dialog.querySelector('button, input, textarea, select, [tabindex]:not([tabindex="-1"])')?.focus();
    }, 0);
    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (showConfirmation) {
          if (!saving) setShowConfirmation(false);
        } else if (quantityDraft) {
          setQuantityDraft(null);
        } else if (modalProduct) {
          setModalProduct(null);
        } else if (showMobileCart) {
          setShowMobileCart(false);
        }
        return;
      }
      if (event.key !== 'Tab') return;
      const dialog = getActiveDialog();
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleDialogKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleDialogKeyDown);
    };
  }, [modalProduct, quantityDraft, saving, showConfirmation, showMobileCart]);

  useEffect(() => {
    let active = true;
    const loadCatalog = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [productResponse, categoryResponse] = await Promise.all([
          getProducts(branchId, { pageSize: 0, sortBy: 'categoryOrder' }),
          getCategories({ branchId, includeAvailability: true }),
        ]);
        if (!active) return;
        const normalizedProducts = normalizePaginatedResponse(productResponse).data || [];
        const normalizedCategories = Array.isArray(categoryResponse) ? categoryResponse : [];
        setProducts(
          getActiveMermaProducts(normalizedProducts, normalizedCategories).filter(
            (product) => Array.isArray(product?.Varieties) && product.Varieties.length > 0
          )
        );
        setCategories(normalizedCategories);
      } catch (error) {
        if (!active) return;
        setLoadError(getErrorMessage(error, 'No se pudieron cargar los productos.'));
      } finally {
        if (active) setLoading(false);
      }
    };
    if (branchId) loadCatalog();
    return () => {
      active = false;
    };
  }, [branchId]);

  const categoriesById = useMemo(
    () => new Map(categories.map((category) => [String(category.id), category])),
    [categories]
  );

  const displayCategories = useMemo(() => {
    const productCategoryIds = new Set(
      products.map(resolveMermaProductCategoryId).filter((id) => id != null).map(String)
    );
    return categories.filter(
      (category) => category?.isActive !== false && productCategoryIds.has(String(category.id))
    ).sort((left, right) =>
      String(left?.name || '').localeCompare(String(right?.name || ''))
    );
  }, [categories, products]);

  useEffect(() => {
    if (selectedCategory === 'all') return;
    if (!displayCategories.some((category) => String(category.id) === String(selectedCategory))) {
      setSelectedCategory('all');
    }
  }, [displayCategories, selectedCategory]);

  const filteredProducts = useMemo(() => {
    const hasSearch = Boolean(searchTerm.trim());
    const categoryFiltered = selectedCategory === 'all' || hasSearch
      ? products
      : products.filter((product) =>
        String(resolveMermaProductCategoryId(product)) === String(selectedCategory)
      );
    if (!hasSearch) return categoryFiltered;
    return filterProductSearchResults(categoryFiltered, searchTerm, { searchMode });
  }, [products, searchMode, searchTerm, selectedCategory]);

  const categoryGroups = useMemo(() => {
    const scopedCategories = selectedCategory === 'all' || searchTerm.trim()
      ? displayCategories
      : displayCategories.filter((category) => String(category.id) === String(selectedCategory));
    return scopedCategories.map((category) => ({
      category,
      products: filteredProducts.filter((product) =>
        String(resolveMermaProductCategoryId(product)) === String(category.id)
      ),
    })).filter((group) => group.products.length > 0);
  }, [displayCategories, filteredProducts, searchTerm, selectedCategory]);

  const totalKg = lines.reduce(
    (sum, line) => sum + normalizeMermaQuantity(line.quantity),
    0
  );
  const payloadItems = buildMermaItemsPayload(lines);

  const openCartSummary = () => {
    const desktopViewport = typeof window !== 'undefined'
      && window.matchMedia?.('(min-width: 768px)').matches;
    if (desktopViewport) {
      setShowDesktopCart(true);
    } else {
      setShowMobileCart(true);
    }
  };

  const openQuantityDraft = (product, variety, existingLine = null) => {
    const category = resolveMermaCategory(product, categoriesById);
    setModalProduct(null);
    setQuantityDraft({
      varietyId: variety.id,
      productId: product.id,
      productName: product.productName,
      varietyName: variety.varietyName,
      imageSrc: variety.varietyImage || product.productImage || PLACEHOLDER_IMAGE,
      categoryId: resolveMermaProductCategoryId(product),
      tracksInventory: Boolean(category?.tracksInventory),
      quantity: existingLine?.quantity || '',
      note: existingLine?.note || '',
    });
  };

  const handleEditLine = (line) => {
    const product = products.find((entry) => Number(entry.id) === Number(line.productId));
    const variety = product?.Varieties?.find((entry) => Number(entry.id) === Number(line.varietyId));
    if (!product || !variety) return;
    openQuantityDraft(product, variety, line);
  };

  const saveQuantityDraft = () => {
    if (!quantityDraft || normalizeMermaQuantity(quantityDraft.quantity) <= 0) return;
    const key = getMermaLineKey(quantityDraft);
    setLines((current) => {
      const existingIndex = current.findIndex((line) => getMermaLineKey(line) === key);
      if (existingIndex < 0) return [...current, quantityDraft];
      return current.map((line, index) => index === existingIndex ? quantityDraft : line);
    });
    setQuantityDraft(null);
    setShowDesktopCart(true);
  };

  const updateLine = (target, patch) => {
    const key = getMermaLineKey(target);
    setLines((current) => current.map((line) =>
      getMermaLineKey(line) === key ? { ...line, ...patch } : line
    ));
  };

  const removeLine = (target) => {
    const key = getMermaLineKey(target);
    setLines((current) => current.filter((line) => getMermaLineKey(line) !== key));
  };

  const requestSave = () => {
    if (!payloadItems.length) {
      notify('Agrega al menos un producto con una cantidad mayor a 0 kg.');
      return;
    }
    if (payloadItems.length !== lines.length) {
      notify('Revisa las cantidades: todos los productos deben tener más de 0 kg.');
      openCartSummary();
      return;
    }
    setShowConfirmation(true);
  };

  const handleSave = async () => {
    if (!payloadItems.length || saving) return;
    const submittedBranchId = branchId;
    const submittedBranchGeneration = branchGenerationRef.current;
    const submittedBranchIsCurrent = () => (
      String(activeBranchRef.current) === String(submittedBranchId)
      && branchGenerationRef.current === submittedBranchGeneration
    );
    setSaving(true);
    try {
      const result = await createInventoryMerma(submittedBranchId, { items: payloadItems });
      if (!submittedBranchIsCurrent()) return;
      setLines([]);
      setShowConfirmation(false);
      setShowMobileCart(false);
      setShowDesktopCart(false);
      const inventorySkippedCount = Number(result?.inventorySkippedCount) || 0;
      if (inventorySkippedCount > 0) {
        notify(
          inventorySkippedCount === 1
            ? 'Merma registrada. 1 producto quedó solo como registro porque no tiene equivalencia de inventario en kg.'
            : `Merma registrada. ${inventorySkippedCount} productos quedaron solo como registro porque no tienen equivalencia de inventario en kg.`
        );
      } else {
        notify('Merma registrada correctamente.');
      }
    } catch (error) {
      if (!submittedBranchIsCurrent()) return;
      notify(getErrorMessage(error, 'No se pudo registrar la merma.'));
    } finally {
      if (submittedBranchIsCurrent()) {
        setSaving(false);
      }
    }
  };

  const renderProduct = (product) => {
    return (
      <PosProductCard
        key={product.id}
        productName={product.productName}
        imageSrc={product.productImage || PLACEHOLDER_IMAGE}
        onClick={() => setModalProduct(product)}
      >
        <span className="flex flex-col items-center gap-1">
          <span className="text-[11px] text-gray-500">
            {product.Varieties.length} {product.Varieties.length === 1 ? 'variedad' : 'variedades'}
          </span>
        </span>
      </PosProductCard>
    );
  };

  return (
    <>
      <div className="flex min-h-[90vh] flex-col bg-gray-100">
        <DocumentHeader
          stickyOffsetClassName="top-14 md:top-0"
          title="Registro de merma"
          emptyClientLabel="Salida de productos por peso"
          showClientType={false}
          showEmptyClientType={false}
          clientMessage="Los productos con inventario se descontarán cuando tengan equivalencia en kg; los demás quedarán solo como registro."
          middleContent={(
            <div className="w-full max-w-[560px]">
              <CategoryFilterBar
                categories={displayCategories}
                selectedCategory={selectedCategory}
                onSelect={setSelectedCategory}
                includeAll
                allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
                loading={loading}
                error={loadError}
                emptyMessage="No hay categorías con productos disponibles."
              />
            </div>
          )}
          rightContent={(
            <div>
              <ProductSearchInput
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                products={products}
                placeholder="Buscar producto..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:ring-2 focus:ring-blue-500 md:text-sm"
              />
              <p className="mt-2 text-xs text-gray-600">
                {products.length} {products.length === 1 ? 'producto' : 'productos'} disponibles
              </p>
            </div>
          )}
          onCartClick={openCartSummary}
        />

        <main className="flex-1 overflow-auto p-4 pb-28">
          <div className="mx-auto max-w-7xl">
            <div className="mb-4 lg:hidden">
              <CategoryFilterBar
                categories={displayCategories}
                selectedCategory={selectedCategory}
                onSelect={setSelectedCategory}
                includeAll
                allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
                loading={loading}
                error={loadError}
              />
            </div>

            {loading ? (
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-16 text-center shadow-sm">
                <InlineLoading label="Cargando productos" />
              </div>
            ) : loadError ? (
              <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
                {loadError}
              </div>
            ) : categoryGroups.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-16 text-center text-sm text-gray-500">
                {searchTerm
                  ? 'No encontramos productos para esta búsqueda.'
                  : 'No hay productos disponibles.'}
              </div>
            ) : (
              <div className="space-y-8">
                {categoryGroups.map(({ category, products: groupProducts }) => (
                  <section key={category.id}>
                    <div className="my-2 flex items-center gap-4">
                      <div className="flex-grow border-t border-gray-300" />
                      <h2 className="whitespace-nowrap text-lg font-bold text-gray-700">
                        {category.name || 'Categoría'}
                      </h2>
                      <div className="flex-grow border-t border-gray-300" />
                    </div>
                    <ProductGrid>{groupProducts.map(renderProduct)}</ProductGrid>
                  </section>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      <div
        className={`fixed bottom-[96px] right-0 top-14 z-30 hidden flex-col overflow-visible transition-[width,background-color] duration-200 ease-out md:top-0 md:flex ${
          showDesktopCart
            ? 'w-[max(440px,33.333333vw)] rounded-l-xl border-l border-t border-gray-200 bg-slate-50 shadow-2xl'
            : 'w-3 border-l border-gray-200 bg-gray-100/70'
        }`}
        onMouseEnter={() => setShowDesktopCart(true)}
      >
        {!showDesktopCart && (
          <button
            type="button"
            onClick={() => setShowDesktopCart(true)}
            className="absolute left-[-3rem] top-1/2 z-10 flex h-14 w-12 -translate-y-1/2 flex-col items-center justify-center gap-0.5 rounded-l-lg border border-r-0 border-gray-200 bg-white text-blue-600 shadow-lg hover:bg-blue-50"
            aria-label="Mostrar merma por registrar"
          >
            <FiChevronLeft size={16} />
            <FiShoppingCart size={18} />
          </button>
        )}
        {showDesktopCart && (
          <div className="flex h-full min-w-0 flex-col gap-3 px-4 py-4">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowDesktopCart(false)}
                className="rounded-md p-2 text-gray-500 hover:bg-gray-200"
                aria-label="Ocultar merma por registrar"
              >
                <FiChevronRight size={20} />
              </button>
            </div>
            <MermaCart
              lines={lines}
              onEdit={handleEditLine}
              onRemove={removeLine}
              onUpdate={updateLine}
              className="min-h-0 flex-1"
            />
          </div>
        )}
      </div>

      <div className="app-fixed-full-width fixed bottom-0 z-20 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setShowMobileCart(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 md:hidden"
          >
            <FiShoppingCart /> {lines.length}
          </button>
          <div className="min-w-0">
            <p className="text-xs text-gray-500">Total por registrar</p>
            <p className="font-semibold text-gray-900">
              {formatKg(totalKg)} · {lines.length} {lines.length === 1 ? 'producto' : 'productos'}
            </p>
          </div>
          <button
            type="button"
            onClick={requestSave}
            disabled={!payloadItems.length || saving}
            className="ml-auto inline-flex items-center gap-2 rounded-lg bg-red-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-red-300"
          >
            <FiTrendingDown /> <span className="hidden sm:inline">Registrar merma</span><span className="sm:hidden">Registrar</span>
          </button>
        </div>
      </div>

      {showMobileCart && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Merma por registrar"
          className="fixed inset-0 z-[1400] flex items-end bg-slate-950/60 md:hidden"
        >
          <div className="flex max-h-[88vh] w-full flex-col rounded-t-2xl bg-gray-100 p-3 shadow-2xl">
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                onClick={() => setShowMobileCart(false)}
                className="rounded-lg p-2 text-gray-600 hover:bg-gray-200"
                aria-label="Cerrar resumen"
              >
                <FiX size={22} />
              </button>
            </div>
            <MermaCart
              lines={lines}
              onEdit={(line) => {
                setShowMobileCart(false);
                handleEditLine(line);
              }}
              onRemove={removeLine}
              onUpdate={updateLine}
              className="min-h-0 flex-1"
            />
          </div>
        </div>
      )}

      <VarietyPickerModal
        product={modalProduct}
        category={modalProduct ? resolveMermaCategory(modalProduct, categoriesById) : null}
        onClose={() => setModalProduct(null)}
        onSelect={(variety) => openQuantityDraft(modalProduct, variety)}
      />
      <QuantityModal
        draft={quantityDraft}
        onCancel={() => setQuantityDraft(null)}
        onDraftChange={(patch) => setQuantityDraft((current) => ({ ...current, ...patch }))}
        onConfirm={saveQuantityDraft}
      />
      <ConfirmMermaModal
        isOpen={showConfirmation}
        lines={lines.filter((line) => normalizeMermaQuantity(line.quantity) > 0)}
        saving={saving}
        onCancel={() => setShowConfirmation(false)}
        onConfirm={handleSave}
      />
    </>
  );
};

export default MermaRegistrationPage;
